import React from 'react'
import classnames from 'classnames'
import { inject, observer } from 'mobx-react'
import Toggle from 'components/common/Toggle'
import ItemList from '../item/ItemList'
import { withTranslation } from 'react-i18next'
import { truncate } from 'lodash'

import EyeIcon from '../../common/icons/EyeIcon'
import { HugeiconsIcon } from '@hugeicons/react'
import { Folder01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons'

const TargetGroup = ({ selectionStore, group, uiStore, dataStore, rightSidebarStore, t }) => {
  const folderStatus = dataStore && group.folder_status
    ? dataStore.folderStatuses.find(status => status.id === group.folder_status)
    : null
  const selectGroupAndLoadPeopleData = async () => {
    // The displayed folder can be a filtered copy. Use the current store group
    // for both selection and fitting so they agree on shapes and saved markers.
    const currentGroup = dataStore.targetGroups.find(candidate => String(candidate.pk) === String(group.pk)) || group
    selectionStore.select(currentGroup)
    selectionStore.hideSubTools()
    // Fit before awaiting sidebar data: a late response must not override a
    // marker or another shape the user focuses while that request is running.
    if (currentGroup && typeof currentGroup.bounds === 'function' && uiStore.zoomToSelected) {
      try {
        const bounds = currentGroup.bounds()
        if (bounds && (bounds.getNorth() !== bounds.getSouth() || bounds.getEast() !== bounds.getWest())) {
          uiStore.setBoundsToItem(currentGroup)
        }
      } catch (e) {
        // no-op
      }
    }
    // Fetch people/income data for right sidebar when a target group is selected.
    if (currentGroup && currentGroup.pk && rightSidebarStore) {
      await rightSidebarStore.fetchPopulationDataForTargetGroup(currentGroup.pk, currentGroup.name)
    }
  }

  const handleTargetGroupMouseDown = (e) => {
    if (e.button !== 0) return
    if (e.target.closest('.target-group__controls') || e.target.closest('.target-group__chev-holder')) {
      return
    }
    const hasCtrlOrShift = e.ctrlKey || e.shiftKey || e.metaKey
    if (hasCtrlOrShift) {
      selectionStore.addSelectedMenuItemGroup(group)
    } else {
      selectionStore.setSelectedMenuItemGroup(group)
    }
  }

  const handleTargetGroupClick = (e) => {
    if (e.target.closest('.target-group__controls') || e.target.closest('.target-group__chev-holder')) {
      return
    }
    const hasCtrlOrShift = e.ctrlKey || e.shiftKey || e.metaKey
    if (hasCtrlOrShift) {
      selectionStore.addSelectedMenuItemGroup(group)
    } else {
      selectionStore.setSelectedMenuItemGroup(group)
      setTimeout(() => {
        selectGroupAndLoadPeopleData()
        uiStore.forceRefreshTiles()
      }, 0)
    }
  }

  const handleCollapseTargetGroup = (e) => {
    e.stopPropagation()

    uiStore.toggleTargetGroupExpanded(group.pk)
  }

  const handleOpenContextMenu = (e) => {
    e.stopPropagation()
    e.preventDefault()
    selectionStore.showContextMenu(group, { data: { group } }, e)
  }

  const handleOpenMenu = (e) => {
    e.stopPropagation()
    e.preventDefault()
    selectionStore.showPopupMenu(group, { data: { group } }, e)
  }

  const handleDragEnter = () => {
    uiStore.dragCounter++
    if (uiStore.dragCounter === 1) {
      group.isDraggedOver = true
    }
  }

  const handleDragLeave = () => {
    uiStore.dragCounter--
    if (uiStore.dragCounter === 0) {
      group.isDraggedOver = false
    }
  }

  const handleDrop = async () => {
    if (selectionStore.hasMultipleSelectedMenuItems) {
      await uiStore.moveGeoBulkOnDrop(group)
      uiStore.resetDragState()
      group.isDraggedOver = false
    } else {
      if (uiStore.draggedMenuItemSourceGroup && uiStore.draggedMenuItemSourceGroup.pk === group.pk) {
        group.isDraggedOver = false
        uiStore.resetDragState()
        return
      }
      await uiStore.moveItemsOnDrop(uiStore.draggedMenuItem, group)
      uiStore.resetDragState()
      group.isDraggedOver = false
    }
  }

  const handleMouseEnter = (e) => {
    if (selectionStore && selectionStore.menuItem && selectionStore.menuItem.visible) {
      return
    }
    if (uiStore && (uiStore.showClickTooltip || uiStore.showDeletePrompt || uiStore.isAddChoiceTypeVisible)) {
      return
    }
    if (uiStore && typeof uiStore.setHoveredTargetGroup === 'function') {
      const buttonElement = e.currentTarget
      const toolsElement = buttonElement.closest('.tools')
      if (toolsElement) {
        uiStore.setHoveredTargetGroup(group.pk, buttonElement, toolsElement, group)
      }
    }
    const realGroup = (dataStore && dataStore.targetGroups && dataStore.targetGroups.find(g => String(g.pk) === String(group.pk))) ||
      (dataStore && dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => String(g.pk) === String(group.pk))) ||
      group
    if (realGroup && typeof realGroup.loadZipCodeBoundaries === 'function' && !realGroup._hasLoadedZipCodesOnHover && (!realGroup.zipCodeBoundaries || realGroup.zipCodeBoundaries.length === 0)) {
      realGroup._hasLoadedZipCodesOnHover = true
      realGroup.loadZipCodeBoundaries().catch(() => {})
    }
    if (group && group !== realGroup && typeof group.loadZipCodeBoundaries === 'function' && !group._hasLoadedZipCodesOnHover && (!group.zipCodeBoundaries || group.zipCodeBoundaries.length === 0)) {
      group._hasLoadedZipCodesOnHover = true
      group.loadZipCodeBoundaries().catch(() => {})
    }
    if (realGroup && typeof realGroup.loadSavedMarkers === 'function' && !realGroup._hasLoadedMarkersOnHover && (!realGroup.savedMarkers || realGroup.savedMarkers.length === 0)) {
      realGroup._hasLoadedMarkersOnHover = true
      realGroup.loadSavedMarkers().catch(() => {})
    }
    if (group && group !== realGroup && typeof group.loadSavedMarkers === 'function' && !group._hasLoadedMarkersOnHover && (!group.savedMarkers || group.savedMarkers.length === 0)) {
      group._hasLoadedMarkersOnHover = true
      group.loadSavedMarkers().catch(() => {})
    }
  }

  const handleMouseLeave = (e) => {
    if (uiStore && typeof uiStore.scheduleClearHoveredTargetGroup === 'function') {
      uiStore.scheduleClearHoveredTargetGroup(250)
    } else if (uiStore && typeof uiStore.clearHoveredTargetGroup === 'function') {
      uiStore.clearHoveredTargetGroup()
    }
  }

  const hasMenuItems = selectionStore.selectedMenuItems && selectionStore.selectedMenuItems.length > 0
  const isSelected = hasMenuItems
    ? selectionStore.selectedMenuItems.some(item => item && String(item.pk || item.id) === String(group.pk))
    : Boolean(
      selectionStore.item === group ||
      (selectionStore.item && String(selectionStore.item.pk) === String(group.pk)) ||
      (selectionStore.type === 'TARGET_GROUP' && selectionStore.item && String(selectionStore.item.pk) === String(group.pk)) ||
      (selectionStore.selectedMenuItemIds && selectionStore.selectedMenuItemIds.some(id => String(id) === String(group.pk)))
    )

  const isGroupSelected = Boolean(
    isSelected ||
    (selectionStore.data && selectionStore.data.group && String(selectionStore.data.group.pk) === String(group.pk)) ||
    (selectionStore.selectedTargetGroup && selectionStore.selectedTargetGroup.item && String(selectionStore.selectedTargetGroup.item.pk) === String(group.pk))
  )

  return (
    <li
      data-target-group-id={group.pk}
      data-group-pk={group.pk}
      style={{
        backgroundColor: isSelected ? '#F1F5F9' : (group.status_color ? `${group.status_color}80` : undefined),
      }}
      className={classnames({
        'target-group': true,
        'group-selected': isGroupSelected,
        selected: isSelected,
        loading: (group.processing && uiStore.draggedMenuItemSourceGroup && uiStore.draggedMenuItemSourceGroup.pk !== group.pk),
        expanded: Boolean(group.expanded),
        'dragged-over': (() => {
          return group.isDraggedOver && uiStore.draggedMenuItemSourceGroup && uiStore.draggedMenuItemSourceGroup.pk !== group.pk
        })()
      })}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      <div
        className='target-group__btn-wrapper'
        onMouseDown={handleTargetGroupMouseDown}
      >
        <div
          className='target-group__chev-holder'
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleCollapseTargetGroup}
        >
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            size={16}
            color='#64748B'
            className={classnames({
              'target-group__chev': true,
              'target-group__chev--expanded': group.expanded
            })}
          />
        </div>
        <button
          className='target-group__btn' type='button'
          data-group-id={group.pk}
          onMouseDown={handleTargetGroupMouseDown}
          onClick={handleTargetGroupClick}
          onContextMenu={handleOpenContextMenu}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onDragOverCapture={(event) => {
            event.stopPropagation()
            event.preventDefault()
          }}
        >

          <HugeiconsIcon
            icon={Folder01Icon}
            size={18}
            color={group.status_color || '#334155'}
            className='target-group__folder-icon'
          />
          {folderStatus && (
            <span
              className={`target-group__status-colour ${dataStore.statusColorType === 'outline' ? 'target-group__status-colour--outline' : ''}`}
              style={dataStore.statusColorType === 'outline'
                ? { borderColor: folderStatus.color }
                : { backgroundColor: folderStatus.color }}
              aria-label={`Status: ${folderStatus.name}`}
              title={folderStatus.name}
            />
          )}
          <span className='target-group__name-holder'>
            <span className='target-group__name'>
              {truncate(group.name, { length: 22 })}
            </span>
          </span>
          <span
            className='target-group__controls'
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Toggle
              icon='three-dots'
              title={t('GLOBAL.HIDE_UNHIDE')}
              extraClasses={['right-toggle no-padding', 'target-group__icon', 'target-group__dots']}
              enabled={selectionStore.isItemMenuOpen ? selectionStore.isItemMenuOpen(group) : (selectionStore.menuItem && selectionStore.menuItem.visible)}
              onClick={handleOpenMenu}
            />
            {
              group.visible
                ? (
                  <EyeIcon
                    onClick={(e) => {
                      e.stopPropagation()
                      uiStore.toggleTargetGroupVisible(group.pk)
                    }}
                    className='target-group__eye'
                  />
                  )
                : (
                  <Toggle
                    icon='eye'
                    title={t('GLOBAL.HIDE_UNHIDE')}
                    extraClasses={['right-toggle', 'target-group__icon', 'target-group__unhide']}
                    enabled={group.visible}
                    onClick={(e) => {
                      e.stopPropagation()
                      uiStore.toggleTargetGroupVisible(group.pk)
                    }}
                  />
                  )
            }
          </span>
        </button>
      </div>
      {
        group.expanded && <ItemList group={group} />
      }
      {group.isDraggedOver && uiStore.draggedMenuItemSourceGroup && uiStore.draggedMenuItemSourceGroup.pk !== group.pk && (
        <div
          onDragOver={(event) => {
            event.preventDefault()
          }}
          onDrop={handleDrop} className='dropzone'
        />
      )}
    </li>
  )
}

export default withTranslation()(inject('selectionStore', 'uiStore', 'dataStore', 'rightSidebarStore')(observer(TargetGroup)))
